import { createSlice, createSelector } from "@reduxjs/toolkit";
import { ofType } from "redux-observable";
import { of } from "rxjs";
import { switchMap, pluck, map, catchError } from "rxjs/operators";
const ALLOUNTRIES_FEATURE_KEY = "allCountries";
const initialAllCountriesState = {
  entities: [],
  loaded: false,
  error: null
};
const baseCountrySettings = {
  requiredRegistrationSmsVerification: 0,
  requiredAge: 18
};
const allCountriesSlice = createSlice({
  name: ALLOUNTRIES_FEATURE_KEY,
  initialState: initialAllCountriesState,
  reducers: {
    getAllCountriesStart: (state, action) => {
      state.loaded = false;
    },
    getAllCountriesSuccess: (state, action) => {
      state.loaded = true;
      state.entities = [...action.payload];
    },
    getAllCountriesFailure: (state, action) => {
      state.loaded = true;
      state.error = action.payload;
    }
  }
});
const { getAllCountriesStart, getAllCountriesSuccess, getAllCountriesFailure } = allCountriesSlice.actions;
const allCountriesReducer = allCountriesSlice.reducer;
const getAllCountriesState = (rootState) => rootState[ALLOUNTRIES_FEATURE_KEY];
const selectAllCountriesEntities = createSelector(
  getAllCountriesState,
  (s) => s.entities
);
const selectRegistrationPossibleCountries = createSelector(
  getAllCountriesState,
  (s) => s.entities.filter((item) => item.isActive)
);
const selectCountryByAlpha2 = createSelector(
  selectRegistrationPossibleCountries,
  (s, alpha2) => (alpha2 || "").toLocaleUpperCase(),
  (s, alpha2) => s.find((country) => country.alpha_2 === alpha2)
);
const selectCountrySmsRequired = createSelector(
  (country) => country,
  (country) => !!+{ ...baseCountrySettings, ...country }.requiredRegistrationSmsVerification
);
const selectCountryMinimalAge = createSelector(
  (country) => country,
  (country) => ({ ...baseCountrySettings, ...country }).requiredAge
);
const selectCountriesByIdMap = createSelector(
  selectRegistrationPossibleCountries,
  (s) => s.reduce(
    (acc, cur) => ({
      ...acc,
      [cur.id]: cur
    }),
    {}
  )
);
const selectCountryBonusPickerSelection = createSelector(
  selectRegistrationPossibleCountries,
  (s, id) => id,
  (s, id) => {
    const { bonusPicker } = s.find(
      (country) => country.alpha_2 === id
    ) || {};
    return bonusPicker;
  }
);
const neededConfirmationCountries = createSelector(
  selectRegistrationPossibleCountries,
  (s) => s.filter((country) => country.needConfirmationModal)
);
const selectCountryByCodeSelect = createSelector(
  selectCountriesByIdMap,
  (s, id) => id || "",
  (countriesMap, id) => countriesMap[+id]
);
const selectClientDefaultCountry = createSelector(
  selectRegistrationPossibleCountries,
  (s, ipData) => ipData || "",
  (s, ipData) => {
    const ipCountry = (ipData == null ? void 0 : ipData.country_code) ? s.find(
      (country) => country.alpha_2.toLowerCase() === ipData.country_code.toLowerCase()
    ) : "";
    return ipCountry ? ipCountry.alpha_2 : null;
  }
);
const selectCountriesById = createSelector(
  selectRegistrationPossibleCountries,
  (s, id) => id,
  (s, id) => s.find((country) => country.alpha_2 === id)
);
const selectCountriesForSelect = createSelector(
  selectRegistrationPossibleCountries,
  (s) => s.map(
    (country) => ({
      text: country.name,
      id: country.alpha_2,
      flagId: country.alpha_2,
      dialCode: country.dialCode,
      dialCodeValue: `${country.dialCode} ${country.alpha_2}`,
      hasPromoCodes: country.hasPromoCodes,
      requiredRegistrationSmsVerification: country.requiredRegistrationSmsVerification,
      hasProvince: country.hasProvince,
      hasFiscalRegions: country.hasFiscalRegions
    })
  )
);
const selectCountriesForSelectMap = createSelector(
  selectCountriesForSelect,
  (s) => s.reduce(
    (acc, cur) => ({
      ...acc,
      [cur.id]: cur
    }),
    {}
  )
);
const selectPrefixesForSelect = createSelector(
  selectCountriesForSelect,
  (s, isMaterial) => isMaterial,
  (s, isMaterial) => s.reduce((acc, { dialCode, id, text }) => {
    if (dialCode) {
      acc.push({
        id: `${dialCode}`,
        flagId: `${id}`,
        text: isMaterial ? `${text}` : `+${dialCode} (${text})`,
        additionalText: isMaterial ? `+${dialCode}` : void 0,
        valueText: `+${dialCode}`
      });
    }
    return acc;
  }, [])
);
const selectIsPrefixExistInCountries = createSelector(
  selectPrefixesForSelect,
  (prefixes, countryCode) => countryCode,
  (prefixes, countryCode) => prefixes.find((prefix) => `${prefix.id}` === `${countryCode}`)
);
const selectPrefixByCountry = createSelector(
  selectPrefixesForSelect,
  (prefixes, countryCode) => countryCode,
  (prefixes, countryCode) => prefixes.find((prefix) => `${prefix.flagId}` === `${countryCode}`)
);
const selectAllCountriesLoaded = createSelector(
  getAllCountriesState,
  (s) => s.loaded
);
const selectCountryFlagByCompetitorId = createSelector(
  [
    (combineCompetitorsSelectors, countryId) => countryId,
    selectAllCountriesEntities
  ],
  (countryId, selectAllCountriesEntities2) => {
    const findCountry = selectAllCountriesEntities2.find(
      (country) => country.id === countryId
    );
    return findCountry == null ? void 0 : findCountry.flagUrl.square;
  }
);
const allCountriesEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(getAllCountriesStart.type),
  switchMap(() => Api.geoClient.getAllCountriesList().pipe(
    pluck("countries"),
    map((countries) => getAllCountriesSuccess(countries)),
    catchError((err) => of(getAllCountriesFailure(err)))
  ))
);
const fetchAllCountries = () => ({ type: getAllCountriesStart.type });
export {
  ALLOUNTRIES_FEATURE_KEY,
  allCountriesEpic,
  allCountriesReducer,
  allCountriesSlice,
  fetchAllCountries,
  getAllCountriesFailure,
  getAllCountriesStart,
  getAllCountriesState,
  getAllCountriesSuccess,
  initialAllCountriesState,
  neededConfirmationCountries,
  selectAllCountriesEntities,
  selectAllCountriesLoaded,
  selectClientDefaultCountry,
  selectCountriesById,
  selectCountriesByIdMap,
  selectCountriesForSelect,
  selectCountriesForSelectMap,
  selectCountryBonusPickerSelection,
  selectCountryByAlpha2,
  selectCountryByCodeSelect,
  selectCountryFlagByCompetitorId,
  selectCountryMinimalAge,
  selectCountrySmsRequired,
  selectIsPrefixExistInCountries,
  selectPrefixByCountry,
  selectPrefixesForSelect,
  selectRegistrationPossibleCountries
};
